import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { CarrierImportService } from '../shared/carrier-import.service';
import { CarrierImportVerificationErrorReason, CarrierImportVerificationResult } from '../shared/carrier-import-verification-result.model';
import { CarrierImportVerificationStatus } from '../shared/carrier-import-verification-status';
import { CarrierImportVerificationRowResult } from '../shared/carrier-import-verification-row-result';
import { CarrierImportParseError } from '../shared/carrier-import-parse-error';
import { CarrierImportValidationError } from '../shared/carrier-import-validation-error';
import { CarrierImportDuplicationError } from '../shared/carrier-import-duplication-error';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';

@Component({
  selector: 'app-carrier-import-verification-tool',
  templateUrl: './carrier-import-verification-tool.component.html',
  styleUrls: ['./carrier-import-verification-tool.component.scss']
})
export class CarrierImportVerificationToolComponent implements OnInit {

  files: File[];
  done = false;
  doing: boolean;
  succeed: boolean;
  invalidFileType = false;
  globalErrorMessage: string;
  verificationResult: CarrierImportVerificationResult;
  VerificationErrorReason = CarrierImportVerificationErrorReason;
  resultTab: 'OK' | 'KO'  = 'OK';
  toggledErrorsView: CarrierImportVerificationRowResult[] = [];

  constructor(
    private carrierImportService: CarrierImportService,
    private translate: TranslateService,
    private loader: LoaderService,
    private toastr: ToastrService,
    private errorHandlerService: ErrorHandlerService,
  ) { }

  ngOnInit(): void {
  }

  changeFile() {
    const fileNameParts  = this.files[0].name.split('.');
    const fileExtension  = fileNameParts[fileNameParts.length - 1];
    this.invalidFileType = fileExtension !== 'xlsx';
    if (this.invalidFileType) {
      this.globalErrorMessage = this.translate.instant('local-loads.import.file.failed.invalid-type',
        { fileExtension: fileExtension.toUpperCase() });
    }
  }

  emptyFile() {
    this.files = [];
    this.verificationResult = null;
  }

  isVerifyEnabled() {
    return !this.files || this.files.length !== 1 || this.invalidFileType;
  }

  verifyData() {
    this.loader.startLoad();
    this.doing = true;

    this.carrierImportService.verifyData(this.files[0])
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.done  = true;
        this.doing = false;
        this.toggledErrorsView = [];
      }))
      .subscribe(result => {
        this.verificationResult = result;

        this.succeed = result.status === CarrierImportVerificationStatus.SUCCEED ||
          result.status === CarrierImportVerificationStatus.PARTIALLY_SUCCEED;
      }, (e: HttpErrorResponse) => {
        console.error(e);
        this.succeed = false;
        this.toastr.error(e.error.message || this.translate.instant('carrier-import.verification-tool.errors.default'));
      });
  }

  getCarrierManagerListFromCarrierRow(carrierRow: CarrierImportVerificationRowResult): string {
    return carrierRow.carrierManagers.map(carrierManager => carrierManager.rowValue.emailAddress).join('; ');
  }

  downloadOkCarriers() {
    this.carrierImportService.downloadOkCarriers(this.verificationResult.carriersRows.filter(row => !row.failed))
      .subscribe(() => {},
        (error) => { this.errorHandlerService.handleHttpError(error, 'carrier-import.verification-tool.errors.download', false)});
  }

  hasNoOkCarriers() {
    return this.verificationResult.carriersRows.filter(row => !row.failed).length === 0;
  }

  shouldShowNameError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.parseErrors.filter(e => e.fieldName === 'name').length > 0 ||
      carrierRow.validationErrors.filter(e => e.fieldName.startsWith('name')).length > 0 ||
      carrierRow.duplicationErrors.filter(e => e.fieldName === 'name').length > 0;
  }

  shouldShowCodeError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.parseErrors.filter(e =>
        e.fieldName === 'CMAWEB code' ||
        e.fieldName === 'SCAC code and Vendor number' ||
        e.fieldName === 'Blueyonder carrier code'
      ).length > 0 ||
      carrierRow.validationErrors.filter(e =>
        e.fieldName.startsWith('CMAWEB code') ||
        e.fieldName.startsWith('SAP SCAC code') ||
        e.fieldName.startsWith('SAP vendor number') ||
        e.fieldName.startsWith('Blueyonder carrier code')
      ).length > 0 ||
      carrierRow.duplicationErrors.filter(e =>
        e.fieldName === 'CMAWEB code' ||
        e.fieldName === 'SAP SCAC code' ||
        e.fieldName === 'SAP vendor number' ||
        e.fieldName === 'Blueyonder carrier code'
      ).length > 0;
  }

  shouldShowRegionError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.parseErrors.filter(e => e.fieldName === 'Region').length > 0 ||
      carrierRow.validationErrors.filter(e => e.fieldName.startsWith('Region')).length > 0;
  }

  shouldShowContactError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.parseErrors.filter(e => e.fieldName === 'Emails for contact and notifications').length > 0;
  }

  shouldShowLanguageError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.parseErrors.filter(e => e.fieldName === 'Preferred language code').length > 0 ||
      carrierRow.validationErrors.filter(e => e.fieldName.startsWith('Preferred language code')).length > 0;
  }

  shouldShowManagerMissingError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.validationErrors.filter(e => e.fieldName.startsWith('Carrier manager')).length > 0;
  }

  shouldShowManagerError(carrierRow: CarrierImportVerificationRowResult): boolean {
    return carrierRow.carrierManagers.filter(managerRow => managerRow.failed).length > 0;
  }

  getCarrierNameErrors(carrierRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = carrierRow.parseErrors.filter(e => e.fieldName === 'name');
    const validationErrors = carrierRow.validationErrors.filter(e => e.fieldName.startsWith('name') && e.message !== 'EMPTY');
    const duplicationErrors = carrierRow.duplicationErrors.filter(e => e.fieldName === 'name');

    parsingErrors.forEach(e => e.type = 'MISSING');
    validationErrors.forEach(e => e.type = 'CMAWEB_DUPLICATE');
    duplicationErrors.forEach(e => e.type = 'FILE_DUPLICATE');

    return [...parsingErrors, ...validationErrors, ...duplicationErrors];
  }

  getCarrierCodeErrors(carrierRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = carrierRow.parseErrors.filter(e =>
      e.fieldName === 'CMAWEB code' ||
      e.fieldName === 'SCAC code and Vendor number' ||
      e.fieldName === 'Blueyonder carrier code'
    );
    const validationErrors = carrierRow.validationErrors.filter(e =>
      (e.fieldName.startsWith('CMAWEB code') ||
      e.fieldName.startsWith('SAP SCAC code') ||
      e.fieldName.startsWith('SAP vendor number') ||
      e.fieldName.startsWith('Blueyonder carrier code')) &&
      e.message !== 'EMPTY'
    );
    const duplicationErrors = carrierRow.duplicationErrors.filter(e =>
      e.fieldName === 'CMAWEB code' ||
      e.fieldName === 'SAP SCAC code' ||
      e.fieldName === 'SAP vendor number' ||
      e.fieldName === 'Blueyonder carrier code'
    );

    parsingErrors.forEach(e => e.type = 'MISSING');
    validationErrors.forEach(e => e.type = 'CMAWEB_DUPLICATE');
    duplicationErrors.forEach(e => e.type = 'FILE_DUPLICATE');

    return [...parsingErrors, ...validationErrors, ...duplicationErrors];
  }

  getCarrierRegionErrors(carrierRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = carrierRow.parseErrors.filter(e => e.fieldName === 'Region');
    const validationErrors = carrierRow.validationErrors.filter(e => e.fieldName.startsWith('Region') && e.message !== 'EMPTY');

    parsingErrors.forEach(e => e.type = 'MISSING');
    validationErrors.forEach(e => e.type = 'INEXISTANT');

    return [...parsingErrors, ...validationErrors];
  }

  getCarrierContactErrors(carrierRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = carrierRow.parseErrors.filter(e => e.fieldName === 'Emails for contact and notifications');

    parsingErrors.forEach(e => e.type = 'INVALID');

    return [...parsingErrors];
  }

  getCarrierLanguageErrors(carrierRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = carrierRow.parseErrors.filter(e => e.fieldName === 'Preferred language code');
    const validationErrors = carrierRow.validationErrors.filter(e => e.fieldName.startsWith('Preferred language code') && e.message !== 'EMPTY');

    parsingErrors.forEach(e => e.type = 'MISSING');
    validationErrors.forEach(e => e.type = 'INEXISTANT');

    return [...parsingErrors, ...validationErrors];
  }

  getCarrierManagerErrors(managerRow: CarrierImportVerificationRowResult):
    (CarrierImportParseError|CarrierImportValidationError|CarrierImportDuplicationError)[] {
    const parsingErrors = managerRow.parseErrors;
    const validationErrors = managerRow.validationErrors.filter(e => e.message !== 'EMPTY');
    const duplicationErrors = managerRow.duplicationErrors;

    parsingErrors.forEach(e => {
      if (e.message === 'Email address invalid') {
        e.type = 'INVALID';
      } else if (e.message === 'Email address is mandatory') {
        e.type = 'MISSING';
      }
    });
    validationErrors.forEach(e => e.type = 'CMAWEB_DUPLICATE');
    duplicationErrors.forEach(e => e.type = 'FILE_DUPLICATE');

    return [...parsingErrors, ...validationErrors, ...duplicationErrors];
  }

  getErrorIdentifier(e: any): string {
    if (e.duplicateIdentities) {
      return e.duplicateIdentities.join(', ');
    } else {
      return e.identity ? e.identity : this.getValidationErrorIdentifier(e);
    }
  }

  getValidationErrorIdentifier(e: any) {
    return e.fieldName.slice(e.fieldName.search('\\[') + 1, -1);
  }

  getCapitalizedErrorField(e: any): string {
    let fieldName = e.fieldName;
    if (e.type === 'CMAWEB_DUPLICATE' || e.type === 'INEXISTANT') {
      fieldName = e.fieldName.slice(0, e.fieldName.search('\\['));
    }
    return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
  }

  toggleErrorsView(carrierRow: CarrierImportVerificationRowResult, event: Event) {
    event.stopPropagation();
    if (this.resultTab === 'KO') {
      const index = this.toggledErrorsView.findIndex(row => row === carrierRow);
      if (index !== -1) {
        this.toggledErrorsView.splice(index, 1);
      } else {
        this.toggledErrorsView.push(carrierRow);
      }
    }
  }

  showErrors(carrierRow: CarrierImportVerificationRowResult) {
    return this.resultTab === 'KO' && carrierRow.failed && this.toggledErrorsView.findIndex(row => row === carrierRow) !== -1;
  }
}
